/**
 * CetusEd Admin Console — System Health Dashboard
 *
 * COMPLIANCE: System health monitoring is required for vendor-hosted mode
 *   to maintain SLA compliance. Health checks include API latency, database
 *   connectivity, storage capacity, and error rates.
 *
 *   SOC 2 — Availability and performance metrics must be recorded and
 *   retained for audit purposes.
 */

import React from 'react';
import { useSystemHealth } from '../../api/hooks';

export const Dashboard: React.FC = () => {
  const { data: health, isLoading, error } = useSystemHealth();

  if (isLoading) return <div className="admin-loading">Loading system health...</div>;

  if (error || !health) {
    return (
      <div className="admin-error" role="alert">
        Unable to retrieve system health data.
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>System Health</h1>
        <span className={`status-badge status-badge--${health.status === 'healthy' ? 'active' : health.status === 'degraded' ? 'past_due' : 'canceled'}`}>
          {health.status}
        </span>
      </div>

      <div className="metric-cards">
        <div className="metric-card">
          <span className="metric-value">{Math.floor(health.uptime / 3600)}h</span>
          <span className="metric-label">Uptime</span>
        </div>
        <div className="metric-card">
          <span className="metric-value">{health.apiLatencyMs}ms</span>
          <span className="metric-label">API Latency</span>
        </div>
        <div className="metric-card">
          <span className="metric-value">{health.dbLatencyMs}ms</span>
          <span className="metric-label">DB Latency</span>
        </div>
        <div className="metric-card">
          <span className="metric-value">{health.activeDistricts}</span>
          <span className="metric-label">Active Districts</span>
        </div>
        <div className="metric-card">
          <span className="metric-value">{health.totalUsers.toLocaleString()}</span>
          <span className="metric-label">Total Users</span>
        </div>
        <div className="metric-card">
          <span className="metric-value">{health.errorRate24h}%</span>
          <span className="metric-label">Error Rate (24h)</span>
        </div>
      </div>

      <div className="detail-card">
        <h3>Storage</h3>
        <p>{health.storageUsed} bytes used</p>
      </div>
    </div>
  );
};
